import React, { useCallback, useEffect, useState } from 'react'
import classnames from 'classnames/bind'
import {
  Modal,
  Icon,
  Text,
  Button,
  hooks,
  constants,
  useApplicationModals,
  // @ts-ignore no types
} from '@adc/ui-adc'
import { isPathModalOpen } from '../../modules/redesign/choosePathModal'
import cls from './styles.scss'

const cx = classnames.bind(cls)

const NUDGE_KEY = 'log_in_to_see_cb'
const NUDGE_DELAY_MS = 60_000
const ELM_ID = 'credit_bid_login_modal'

// MERCH-12: prompts logged-out users to log in to see the credit bid. Owns its
// own display logic (60s timer + once-per-day nudge recurrence).
// Desktop: centered Modal. Mobile: SlideUpPanel (via Modal slideUp prop).

interface CreditBidLoginModalProps {
  isLoggedOut: boolean
  isCreditBidRevealed: boolean
  isAuctionActive: boolean
  isTrusteeSale: boolean
}

const CreditBidLoginModal = ({
  isLoggedOut,
  isCreditBidRevealed,
  isAuctionActive,
  isTrusteeSale,
}: CreditBidLoginModalProps) => {
  const { data, actions } = hooks.useWebApplicationNudges()
  const modals = useApplicationModals()
  const breakpoint = hooks.useBreakpoints()
  const [isVisible, setIsVisible] = useState(false)

  const isMobile = breakpoint === 'mobile'

  const isEligible =
    isLoggedOut &&
    isCreditBidRevealed &&
    isAuctionActive &&
    isTrusteeSale &&
    data.active.has(NUDGE_KEY)

  useEffect(() => {
    if (!isEligible) return undefined
    const timer = setTimeout(() => {
      // Record the nudge whether or not we show it, so an auth modal already
      // open when the timer fires still counts as the once-per-day trigger.
      actions.nudge(NUDGE_KEY)
      const activeModalId = modals.getActiveModalId?.()
      // Don't stack on top of the auth modal or the dual-track choose-a-path
      // modal (open-state published at module scope by ChoosePathModal).
      if (activeModalId === 'authentication' || isPathModalOpen()) return
      setIsVisible(true)
    }, NUDGE_DELAY_MS)
    return () => clearTimeout(timer)
  }, [isEligible, modals, actions])

  useEffect(() => {
    if (!isLoggedOut) setIsVisible(false)
  }, [isLoggedOut])

  const handleDismiss = useCallback(() => setIsVisible(false), [])

  const handleLogIn = useCallback(() => {
    setIsVisible(false)
    modals.open('authentication', { authInitialActive: 'login' })
  }, [modals])

  if (!isVisible) return null

  return (
    <Modal
      open
      size='tiny'
      elmId={ELM_ID}
      className={cx('modal')}
      onClose={handleDismiss}
      closeIcon={false}
      fullScreen='never'
      slideUp='mobileOnly'
    >
      <Modal.Content>
        <div className={cx('title-row')}>
          <Icon
            name='badge-dollar'
            iconStyle='solid'
            color={constants.ColorNames.AdcLightGreen}
            size={isMobile ? 'big' : 'large'}
          />
          <Text
            size={isMobile ? 'huge' : 'large'}
            weight='heavy'
            color={constants.ColorNames.GrayDark}
          >
            Est. Credit Bid is Available
          </Text>
        </div>
        <Text
          as='p'
          size='small'
          color={constants.ColorNames.Gray}
          className={cx('description')}
        >
          This is the bid to beat. The highest bid above it wins.
        </Text>
        <div className={cx('login-button-wrapper')}>
          <Button
            role='primary'
            isStretch
            elmId={`${ELM_ID}_login`}
            onClick={handleLogIn}
          >
            Log In to See
          </Button>
        </div>
        <div className={cx('dismiss-button')}>
          <Button
            role='tertiary'
            isStretch
            elmId={`${ELM_ID}_dismiss`}
            onClick={handleDismiss}
          >
            Remind Me Later
          </Button>
        </div>
      </Modal.Content>
    </Modal>
  )
}

export default CreditBidLoginModal
